import { useEffect, useRef } from "react";

const FOKUSERBARE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Faelles opfoersel for de to dialoger i appen (fejlrapport og
 * sammenligningsark). Compare havde Escape og scroll-laas i forvejen,
 * FejlModal havde ingen af delene, og ingen af dem flyttede fokus. Den slags
 * skal opfoere sig ens, ellers er tastaturbrug et lotteri.
 *
 * Hooken giver:
 * - Escape lukker, men kun den dialog fokus faktisk staar i.
 * - Sidens scroll laases mens dialogen er aaben.
 * - Fokus flyttes ind i dialogen ved aabning.
 * - Tab bliver inde i dialogen.
 * - Fokus gaar tilbage til det element der aabnede dialogen, ved luk.
 *
 * Returnerer en ref der skal saettes paa selve dialog-elementet. Elementet skal
 * have tabIndex={-1}, saa der er noget at give fokus til hvis dialogen aabner
 * uden fokuserbart indhold.
 *
 * `onClose` laeses gennem en ref, saa en ny inline-funktion fra kaldstedet ikke
 * genstarter effekten og stjaeler fokus tilbage ved hver render.
 */
export function useDialog<T extends HTMLElement>(onClose: () => void) {
  const ref = useRef<T>(null);
  const lukRef = useRef(onClose);

  useEffect(() => {
    lukRef.current = onClose;
  });

  useEffect(() => {
    const aabnetAf = document.activeElement as HTMLElement | null;
    const node = ref.current;

    const synlige = () =>
      Array.from(node?.querySelectorAll<HTMLElement>(FOKUSERBARE) ?? []).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement,
      );

    (synlige()[0] ?? node)?.focus();

    const onKey = (e: KeyboardEvent) => {
      // Ligger der to dialoger oven paa hinanden, er det kun den fokus staar i
      // der reagerer. Fokusfaelden nedenfor sikrer at det altid er den oeverste.
      if (!node || !node.contains(document.activeElement)) return;

      if (e.key === "Escape") {
        lukRef.current();
        return;
      }
      if (e.key !== "Tab") return;

      const items = synlige();
      if (items.length === 0) {
        e.preventDefault();
        node.focus();
        return;
      }
      const foerste = items[0];
      const sidste = items[items.length - 1];
      const aktiv = document.activeElement;
      if (e.shiftKey && (aktiv === foerste || aktiv === node)) {
        e.preventDefault();
        sidste.focus();
      } else if (!e.shiftKey && aktiv === sidste) {
        e.preventDefault();
        foerste.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    const forrigeOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = forrigeOverflow;
      // Kun hvis knappen der aabnede dialogen stadig findes i dokumentet.
      if (aabnetAf?.isConnected) aabnetAf.focus();
    };
  }, []);

  return ref;
}
